/* eslint-disable react/jsx-no-comment-textnodes */
import { titleize } from '@common/helpers/strings';
import SearchPagination from '@react/search/components/SearchPagination';
import Footer from '@react/shared/components/Footer';
import { RESOURCE_CENTER_PATH } from '@sly/core/constants/dashboardAppPaths';
import { getCanonicalPath } from '@sly/frontend/react/search/helpers';
import cx from 'classnames';
import { object } from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import ArticlePreview from 'sly/resourceCenter/components/ArticlePreview';
import Header from 'sly/resourceCenter/components/Header';
import Helmet from 'sly/resourceCenter/components/Helmet';
import ResourceCenterChatBotWrapper from 'sly/resourceCenter/components/ResourceCenterChatBotWrapper';
import SubscribeEmail from 'sly/resourceCenter/components/SubscribeEmail';
import {
  ARTICLES_PER_PAGE,
  ARTICLES_RANGE_FOR_PAGINATION,
  getPaginationProperties,
} from 'sly/resourceCenter/helpers';
import { usePrefetch } from 'sly/services/api';
import { pageNames, useEvents } from 'sly/services/events';

const meiliPagination = (
  guideTotalPage,
  guidesHits,
  currentPage,
  totalPages,
  totalCount,
  basePath
) => {
  const [linkRel, filteredParams, rejectParams] = getCanonicalPath(
    basePath,
    {},
    currentPage,
    totalPages
  );

  // We show up to 3 guides and 15 articles per page. This first assignment captures the case when we have 3 guides and 15 articles. Meaning the current page is less than the total pages of guides we have
  const res = {
    basePath,
    filteredParams,
    rejectParams,
    linkRel,
    current: currentPage,
    total: totalPages,
    count: totalCount,
    start: (currentPage - 1) * ARTICLES_RANGE_FOR_PAGINATION,
    end: Math.min(currentPage * ARTICLES_RANGE_FOR_PAGINATION, totalCount),
  };

  // This next case captures when we are on the last page of guides or after and we are only showing articles meaning 15 per page + whatever guides we have left
  if (currentPage >= guideTotalPage) {
    res.start = guidesHits + (currentPage - 1) * ARTICLES_PER_PAGE;
    res.end = Math.min(
      guidesHits + currentPage * ARTICLES_PER_PAGE,
      totalCount
    );
  }

  return res;
};

const Search = ({
  query,
  tag,
  hits,
  totalHits,
  guidePages,
  guideHits,
  totalPages,
  requestParams,
}) => {
  const { searchBy } = query;
  const { events } = useEvents();
  useEffect(() => {
    events.page(pageNames.ResourceCenterSearch, {});
  }, []);

  const pageNumber = parseInt(query['page-number'], 10) || 1;

  const {
    requestInfo: { result },
  } = usePrefetch('getArticle', requestParams, {
    shouldBail: !tag,
  });

  const articles = hits || result?.data;

  const articlesCount = totalHits || result?.meta?.pagination?.total;

  const titleText = useMemo(() => {
    if (!articlesCount) {
      return `No results for “${searchBy}”`;
    }
    const processedTag = tag ? tag.split(' ').map(titleize).join(' ') : null;
    const forString = tag ? `tag "${processedTag}"` : `"${searchBy}"`;
    if (articlesCount === 1) {
      return `1 result for ${forString}`;
    }
    return `${articlesCount} results for ${forString}`;
  }, [articlesCount, searchBy, tag]);

  const pagination = tag
    ? getPaginationProperties({
        basePath: tag
          ? `${RESOURCE_CENTER_PATH}/tag/${tag}`
          : `${RESOURCE_CENTER_PATH}/search/${encodeURI(searchBy)}`,
        count: articlesCount,
        current: pageNumber,
      })
    : meiliPagination(
        guidePages,
        guideHits,
        pageNumber,
        totalPages,
        articlesCount,
        tag
          ? `${RESOURCE_CENTER_PATH}/tag/${tag}`
          : `${RESOURCE_CENTER_PATH}/search/${encodeURI(searchBy)}`
      );

  return (
    <>
      <ResourceCenterChatBotWrapper>
        <Helmet
          path={pagination?.linkRel?.canonical}
          title={`Expert resources on ${titleize(tag) || searchBy} | Seniorly`}
          shortDescription={`Read articles written by industry experts on ${
            tag || searchBy
          } , senior living,  assisted living, senior lifestyles and more on Seniorly.`}
          articlesCount={articlesCount}
        />

        <Header />

        <div
          className='mx-m md:mx-auto md:w-[42.5rem] lg:w-[64.5rem]'
          data-tp-id='gen-Block-a4d8dcf3-3ea4-40a7-b816-c77819bdc105'
        >
          <h1
            className={cx(
              'font-t-l mt-l',
              !articlesCount ? 'mb-xxl md:mb-xxxl lg:mb-60' : 'mb-l md:mb-xxl',
              'md:mt-xxl'
            )}
            data-tp-id='gen-Block-69b932f5-000e-414a-a2bf-52508ef79425'
          >
            {titleText}
          </h1>

          {!!articles?.length && (
            <>
              <div
                className={cx(
                  'mb-xxl grid justify-center',
                  'grid-cols-[20.5rem] gap-m',
                  'md:grid-cols-[20.5rem_20.5rem] md:gap-l',
                  'lg:grid-cols-[20.5rem_20.5rem_20.5rem]'
                )}
                data-tp-id='gen-Grid-397e7591-04dc-4683-9a0b-c9679e262ab1'
              >
                {articles.map(
                  ({
                    id,
                    mainTopic,
                    tagsList,
                    title,
                    shortDescription,
                    mainImg,
                    slug,
                    livingTypesLink,
                    description,
                  }) => (
                    <ArticlePreview
                      {...{
                        alternativeText: mainImg?.alternativeText,
                        title,
                        shortDescription: description || shortDescription,
                        path: mainImg?.path,
                        topic: mainTopic,
                        tagsList,
                        to:
                          livingTypesLink ||
                          `${RESOURCE_CENTER_PATH}/${mainTopic?.slug}/${slug}`,
                        key: id,
                      }}
                      key={id}
                    />
                  )
                )}
              </div>

              <div
                className='mx-auto mb-xxl w-max'
                data-tp-id='gen-Block-d793706d-35cf-4270-bffc-c1e048cefc92'
              >
                {articlesCount > ARTICLES_RANGE_FOR_PAGINATION && (
                  <SearchPagination pagination={pagination} />
                )}
              </div>
            </>
          )}
        </div>

        <SubscribeEmail />

        <Footer />
      </ResourceCenterChatBotWrapper>
    </>
  );
};

Search.propTypes = {
  query: object,
};

export default Search;
